import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaArrowLeft, FaPlus } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BackButton } from '../common/styles';
import { MyCV } from '../MyActivities';
import useCVStoreEmployee from '../../store/cvStoreEmployee';
import useGridNavigation from '../../hooks/useGridNavigation';
import CVGrid from '../CVRepository/components/CVGrid';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  MainContainer,
  HeaderButton,
} from '../CVRepository/styles';

const CVMyRepository = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams(); // Get the CV ID from URL params
  const gridRef = useRef(null);
  
  // Get all state and actions from the employee store
  const {
    employeeCVs: cvs,
    isLoading,
    isLoadingMore,
    pageInfo,
    fetchEmployeeCVs,
    resetState
  } = useCVStoreEmployee();

  // Create a callback for loading more data
  const loadMoreCallback = useCallback(() => {
    // When loading more data via scroll, we never want to reset the data
    // Pass false explicitly to ensure we're appending data, not replacing
    if (user?.employee?.id) {
      return fetchEmployeeCVs(user.employee.id, false);
    }
    return Promise.resolve(null);
  }, [fetchEmployeeCVs, user?.employee?.id]);

  // Callback for when navigating away from this section
  const handleNavigateAway = useCallback(() => {
    // Reset any state needed when leaving the My CVs section
    // (Currently no specific cleanup needed)
  }, []);

  // Use the GridNavigation hook with My CVs routes
  const {
    showLoadingSpinner,
    handleCombinedGridScroll,
    handleItemClick,
    handleBackToItems,
    handleAddNewItem,
    handleCustomBack,
    shouldHideContent,
    loadMoreData,
    isNewItem,
    shouldLoadData,
    shouldForceReload,
    isReturningFromDetailPage,
    hasItems
  } = useGridNavigation({
    gridRef,
    items: cvs,
    columnCount: 3,
    rowHeight: 230,
    routes: {
      list: '/cv-repository/my-cvs',
      detail: (id) => `/cv-repository/my-cvs/${id}`,
      new: '/cv-repository/my-cvs/new',
      additionalRoutes: []
    },
    loadMoreDataCallback: loadMoreCallback,
    pageInfo,
    isLoadingMore,
    onNavigateAway: handleNavigateAway
  });
  
  // First effect: Always reset the store on mount
  useEffect(() => {
    // This effect only resets the store to ensure clean state
    // It runs on every mount but has no dependencies so it won't re-run
    resetState();
  }, [resetState]);

  // Second effect: Load data when needed
  useEffect(() => {
    // If we have no employee ID, we can't load data
    if (!user?.employee?.id) {
      return;
    }
    
    // Only run this effect when we're on the main repository view
    // using the centralized shouldLoadData function
    if (!shouldLoadData()) {
      return;
    }
    
    // CRITICAL FIX: Prevent reloading data if we already have items
    // This prevents infinite reload cycles when using cursor-based pagination
    if (hasItems && !isLoadingMore && !shouldForceReload()) {
      return;
    }

    // FIXED: Only force reload when items array is empty
    // The previous logic always forced reload during normal browsing
    const forceReload = shouldForceReload();
    
    // Fetch data with reset parameter based on whether we need to force reload
    fetchEmployeeCVs(user.employee.id, forceReload);
    
  // Dependency array properly excludes cvs.length to prevent reload cycles during pagination
  }, [fetchEmployeeCVs, shouldLoadData, shouldForceReload, isReturningFromDetailPage, user, hasItems, isLoadingMore]);

  // Render CVs grid
  const renderCVs = () => {
    return (
      <CVGrid
        cvs={cvs}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        pageInfo={pageInfo}
        handleItemClick={handleItemClick}
        handleGridScroll={handleCombinedGridScroll}
        showLoadingSpinner={showLoadingSpinner}
        shouldHideContent={shouldHideContent}
        loadMoreData={loadMoreData}
        gridRef={gridRef}
        onView={handleItemClick}
      />
    );
  };

  // If showing the Add CV form or CV detail, render the MyCV component
  if (id || isNewItem) {
    return (
      <MyCV 
        cvId={id} 
        employee={user?.employee}
        onBack={isNewItem ? handleCustomBack : handleBackToItems}
        disableEmployeeChange={true}
        autoLinkEmployee={isNewItem}
      />
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>My CVs</Title>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '16px' }}>
          <HeaderButton onClick={handleAddNewItem}>
            <FaPlus /> Add CV
          </HeaderButton>
        </div>
      </Header>
      <Content>
        <MainContainer>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0
          }}>
            <span style={{ fontWeight: 600 }}>
              {pageInfo?.totalCount || 0}
            </span>
            {pageInfo?.totalCount === 1 ? 'CV' : 'CVs'} found
          </div>

          {renderCVs()}
        </MainContainer>
      </Content>
    </Container>
  );
};

export default CVMyRepository; 